import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useUser } from '../contexts/UserContext';
import { useToast } from '../contexts/ToastContext';
import { hearingTestApi } from '../services/api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// ─── Constants ───────────────────────────────────────────────────────────────

const FREQUENCIES = [125, 250, 500, 750, 1000, 1500, 2000, 3000, 4000, 6000, 7000, 8000, 9000, 10000, 11000, 12000];

/**
 * Gain range for the ramp.
 * GAIN_MIN = 0.00001 ≈ -100 dBFS — completely inaudible start.
 * GAIN_MAX = 1.0    = 0 dBFS
 * This 100 dB range ensures even users with very sensitive hearing won't hear the
 * tone at the beginning, making threshold detection meaningful.
 */
const GAIN_MIN = 0.00001;
const GAIN_MAX = 1.0;
const RAMP_DURATION = 12;      // seconds for full ramp
const MIN_ATTEMPTS = 3;
const MAX_ATTEMPTS = 5;
const DB_VARIANCE_THRESHOLD = 6;  // ±6 dB tolerance — matches clinical audiometry standards
const PAUSE_BETWEEN_MS = 1200;    // silence gap between repetitions
const FREQ_JITTER = 0.07;         // ±7% pitch variance per playback

// ─── Helpers ─────────────────────────────────────────────────────────────────

function shuffled<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/**
 * Build a queue that interleaves all frequencies across MAX_ATTEMPTS rounds.
 * Each round is independently shuffled, so no frequency ever appears twice
 * in a row between adjacent rounds (extremely unlikely), and never back-to-back
 * within a round. Result: 55 entries (11 freqs × 5 rounds), fully mixed.
 */
function buildTestQueue(): number[] {
  const rounds: number[][] = [];
  let prevLast: number | null = null;
  for (let r = 0; r < MAX_ATTEMPTS; r++) {
    let round = shuffled([...FREQUENCIES]);
    // If first item of this round equals last item of previous, rotate by 1
    if (prevLast !== null && round[0] === prevLast) {
      round = [...round.slice(1), round[0]];
    }
    rounds.push(round);
    prevLast = round[round.length - 1];
  }
  return rounds.flat();
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/** Check consistency in dB space — a ±6 dB spread is within normal reaction-time variability. */
function isConsistent(measurements: number[]): boolean {
  if (measurements.length < 2) return false;
  const dbValues = measurements.map(g => 20 * Math.log10(Math.max(g, GAIN_MIN)));
  const med = median(dbValues);
  return dbValues.every(v => Math.abs(v - med) <= DB_VARIANCE_THRESHOLD);
}

function isFreqDone(freq: number, measurements: Record<number, number[]>): boolean {
  const m = measurements[freq] || [];
  return m.length >= MAX_ATTEMPTS || (m.length >= MIN_ATTEMPTS && isConsistent(m));
}

function countDone(measurements: Record<number, number[]>): number {
  return FREQUENCIES.filter(f => isFreqDone(f, measurements)).length;
}

/** Map gain to approximate dBHL (0 = excellent, 80 = severe loss). */
function gainToDbHL(gain: number): number {
  const clamped = Math.max(GAIN_MIN, Math.min(GAIN_MAX, gain));
  return ((Math.log10(clamped) - Math.log10(GAIN_MIN)) /
          (Math.log10(GAIN_MAX)  - Math.log10(GAIN_MIN))) * 80;
}

/** Apply a small random pitch offset so no two presentations are identical. */
function jitter(nominalHz: number): number {
  const factor = 1 + (Math.random() * 2 - 1) * FREQ_JITTER;
  return nominalHz * factor;
}

function freqLabel(hz: number): string {
  return hz >= 1000 ? `${hz / 1000}k` : String(hz);
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Phase = 'instructions' | 'testing' | 'ear_break' | 'results';
type Ear = 'left' | 'right';

// ─── Component ───────────────────────────────────────────────────────────────

const HearingTest: React.FC = () => {
  const { user } = useUser();
  const { showSuccess, showError } = useToast();

  // ── Phase / ear / mode ───────────────────────────────────────────────────
  const [phase, setPhase] = useState<Phase>('instructions');
  const [currentEar, setCurrentEar] = useState<Ear>('left');
  const currentEarRef = useRef<Ear>('left');
  const testModeRef = useRef<'both' | 'left' | 'right'>('both');

  // ── Test queue ────────────────────────────────────────────────────────────
  // Mutable refs drive the audio callbacks; React state is for rendering only.
  const testQueueRef = useRef<number[]>([]);
  const queueIndexRef = useRef(0);
  const measurementsRef = useRef<Record<number, number[]>>({});
  const currentFreqRef = useRef<number | null>(null);

  // Display state (synced from refs via setters)
  const [displayMeasurements, setDisplayMeasurements] = useState<Record<number, number[]>>({});
  const [currentFreq, setCurrentFreq] = useState<number | null>(null);

  // ── Thresholds (final) ────────────────────────────────────────────────────
  const [leftThresholds, setLeftThresholds] = useState<Record<number, number>>({});
  const [rightThresholds, setRightThresholds] = useState<Record<number, number>>({});
  const leftThresholdsRef = useRef<Record<number, number>>({});
  const rightThresholdsRef = useRef<Record<number, number>>({});

  // ── Audio ─────────────────────────────────────────────────────────────────
  const [isPlaying, setIsPlaying] = useState(false);
  const isPlayingRef = useRef(false);
  const [rampProgress, setRampProgress] = useState(0);

  const audioCtxRef = useRef<AudioContext | null>(null);
  const oscillatorRef = useRef<OscillatorNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const toneStartCtxTimeRef = useRef(0);
  const rafRef = useRef<number>(0);
  const autoEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Save ──────────────────────────────────────────────────────────────────
  const [isSaving, setIsSaving] = useState(false);
  const [savedResultId, setSavedResultId] = useState<number | null>(null);
  const testStartTimeRef = useRef<Date | null>(null);

  // ── Audio helpers ─────────────────────────────────────────────────────────

  const stopTone = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    if (autoEndTimerRef.current) clearTimeout(autoEndTimerRef.current);
    try { oscillatorRef.current?.stop(); } catch (_) { /* already stopped */ }
    oscillatorRef.current?.disconnect();
    gainRef.current?.disconnect();
    oscillatorRef.current = null;
    gainRef.current = null;
    audioCtxRef.current = null;
    isPlayingRef.current = false;
    setIsPlaying(false);
    setRampProgress(0);
  }, []);

  const playTone = useCallback((frequency: number, ear: Ear) => {
    stopTone();

    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const panner = ctx.createStereoPanner();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(jitter(frequency), ctx.currentTime);
    panner.pan.setValueAtTime(ear === 'left' ? -1 : 1, ctx.currentTime);

    // Ramp from near-silence to full volume
    gain.gain.setValueAtTime(GAIN_MIN, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(GAIN_MAX, ctx.currentTime + RAMP_DURATION);

    osc.connect(gain);
    gain.connect(panner);
    panner.connect(ctx.destination);
    osc.start();

    audioCtxRef.current = ctx;
    oscillatorRef.current = osc;
    gainRef.current = gain;
    toneStartCtxTimeRef.current = ctx.currentTime;
    isPlayingRef.current = true;
    setIsPlaying(true);

    // Animate progress bar
    const animate = () => {
      if (!audioCtxRef.current) return;
      const p = Math.min((audioCtxRef.current.currentTime - toneStartCtxTimeRef.current) / RAMP_DURATION, 1);
      setRampProgress(p);
      if (p < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);

    // Auto-record max threshold if user doesn't respond in time
    autoEndTimerRef.current = setTimeout(() => {
      recordMeasurement(GAIN_MAX);
    }, (RAMP_DURATION + 0.5) * 1000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopTone]); // recordMeasurement intentionally omitted — assigned via ref below to avoid circular dependency

  // ── Core measurement logic ────────────────────────────────────────────────

  /** Advance through the queue, skipping already-done frequencies. */
  const advanceQueue = useCallback((
    updatedMeasurements: Record<number, number[]>,
    fromIndex: number,
    ear: Ear
  ) => {
    const queue = testQueueRef.current;
    let nextIdx = fromIndex + 1;
    while (nextIdx < queue.length && isFreqDone(queue[nextIdx], updatedMeasurements)) {
      nextIdx++;
    }

    if (nextIdx >= queue.length || countDone(updatedMeasurements) === FREQUENCIES.length) {
      // All frequencies measured for this ear — compute final thresholds
      const thresholds: Record<number, number> = {};
      for (const f of FREQUENCIES) {
        const m = updatedMeasurements[f] || [];
        thresholds[f] = m.length > 0 ? median(m) : GAIN_MAX;
      }

      if (ear === 'left') {
        leftThresholdsRef.current = thresholds;
        setLeftThresholds(thresholds);
        setPhase(testModeRef.current === 'left' ? 'results' : 'ear_break');
      } else {
        rightThresholdsRef.current = thresholds;
        setRightThresholds(thresholds);
        setPhase('results');
      }
      return;
    }

    queueIndexRef.current = nextIdx;
    const nextFreq = queue[nextIdx];
    currentFreqRef.current = nextFreq;
    setCurrentFreq(nextFreq);
    setTimeout(() => playTone(nextFreq, ear), PAUSE_BETWEEN_MS);
  }, [playTone]);

  const recordMeasurement = useCallback((gain: number) => {
    if (autoEndTimerRef.current) clearTimeout(autoEndTimerRef.current);
    stopTone();

    const freq = currentFreqRef.current;
    if (freq === null) return;

    const prev = measurementsRef.current[freq] || [];
    const updated = { ...measurementsRef.current, [freq]: [...prev, gain] };
    measurementsRef.current = updated;
    setDisplayMeasurements({ ...updated });

    advanceQueue(updated, queueIndexRef.current, currentEarRef.current);
  }, [stopTone, advanceQueue]);

  // Give playTone access to recordMeasurement via a stable ref
  const recordMeasurementRef = useRef(recordMeasurement);
  useEffect(() => { recordMeasurementRef.current = recordMeasurement; }, [recordMeasurement]);

  // ── "I Can Hear It" handler ───────────────────────────────────────────────

  const handleCanHear = useCallback(() => {
    if (!audioCtxRef.current || !isPlayingRef.current) return;
    const elapsed = audioCtxRef.current.currentTime - toneStartCtxTimeRef.current;
    const progress = Math.min(elapsed / RAMP_DURATION, 1);
    const gain = GAIN_MIN * Math.pow(GAIN_MAX / GAIN_MIN, progress);
    recordMeasurementRef.current(gain);
  }, []);

  // ── Test lifecycle ─────────────────────────────────────────────────────────

  const initEar = useCallback((ear: Ear) => {
    const queue = buildTestQueue();
    testQueueRef.current = queue;
    queueIndexRef.current = 0;
    measurementsRef.current = {};
    setDisplayMeasurements({});
    currentEarRef.current = ear;
    setCurrentEar(ear);

    const firstFreq = queue[0];
    currentFreqRef.current = firstFreq;
    setCurrentFreq(firstFreq);

    setTimeout(() => playTone(firstFreq, ear), 600);
  }, [playTone]);

  const startTest = useCallback((mode: 'both' | 'left' | 'right' = 'both') => {
    leftThresholdsRef.current = {};
    rightThresholdsRef.current = {};
    setLeftThresholds({});
    setRightThresholds({});
    setSavedResultId(null);
    testStartTimeRef.current = new Date();
    testModeRef.current = mode;
    setPhase('testing');
    initEar(mode === 'right' ? 'right' : 'left');
  }, [initEar]);

  const startRightEar = useCallback(() => {
    setPhase('testing');
    initEar('right');
  }, [initEar]);

  useEffect(() => () => stopTone(), [stopTone]);

  // ── Save ──────────────────────────────────────────────────────────────────

  const saveResults = useCallback(async () => {
    if (!user) {
      showError('Not logged in', 'Please sign in to save your results.');
      return;
    }
    setIsSaving(true);
    try {
      const durationSeconds = testStartTimeRef.current
        ? Math.round((Date.now() - testStartTimeRef.current.getTime()) / 1000)
        : undefined;

      const toStr = (obj: Record<number, number>): Record<string, number> =>
        Object.fromEntries(Object.entries(obj).map(([k, v]) => [String(k), v]));

      const result = await hearingTestApi.create({
        left_ear_thresholds: toStr(leftThresholdsRef.current),
        right_ear_thresholds: toStr(rightThresholdsRef.current),
        test_duration_seconds: durationSeconds,
      });
      setSavedResultId(result.id);
      showSuccess('Test saved', 'Your audiogram has been saved to your profile.');
    } catch {
      showError('Save failed', 'Could not save results. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [user, showSuccess, showError]);

  // ── Chart ─────────────────────────────────────────────────────────────────

  const chartData = {
    labels: FREQUENCIES.map(freqLabel),
    datasets: [
      {
        label: 'Left Ear',
        data: FREQUENCIES.map(f => {
          const g = leftThresholds[f];
          return g !== undefined ? gainToDbHL(g) : null;
        }),
        borderColor: '#3B82F6',
        backgroundColor: '#3B82F6',
        pointStyle: 'crossRot' as const,
        pointRadius: 9,
        pointHoverRadius: 11,
        borderWidth: 2,
        tension: 0,
        spanGaps: false,
      },
      {
        label: 'Right Ear',
        data: FREQUENCIES.map(f => {
          const g = rightThresholds[f];
          return g !== undefined ? gainToDbHL(g) : null;
        }),
        borderColor: '#EF4444',
        backgroundColor: '#EF4444',
        pointStyle: 'circle' as const,
        pointRadius: 7,
        pointHoverRadius: 9,
        borderWidth: 2,
        tension: 0,
        spanGaps: false,
      },
      {
        label: 'Normal hearing (25 dBHL)',
        data: FREQUENCIES.map(() => 25),
        borderColor: '#10B981',
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderDash: [6, 4],
        pointRadius: 0,
        tension: 0,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'bottom' as const },
      title: {
        display: true,
        text: 'Audiogram — Hearing Thresholds by Frequency',
        font: { size: 15 },
      },
      tooltip: {
        callbacks: {
          label: (ctx: any) =>
            ctx.dataset.pointRadius === 0
              ? ctx.dataset.label
              : `${ctx.dataset.label}: ${Math.round(ctx.parsed.y)} dBHL`,
        },
      },
    },
    scales: {
      x: { title: { display: true, text: 'Frequency (Hz)' } },
      y: {
        reverse: true,
        min: -10,
        max: 90,
        title: { display: true, text: 'Hearing Level (dBHL)' },
        ticks: { callback: (v: any) => `${v} dB` },
      },
    },
  };

  // ── Render: Instructions ──────────────────────────────────────────────────

  const renderInstructions = () => (
    <div className="max-w-2xl mx-auto text-center space-y-6">
      <div className="w-20 h-20 bg-auris-teal bg-opacity-15 rounded-full flex items-center justify-center mx-auto">
        <svg className="w-10 h-10 text-auris-teal" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.348 14.651a3.75 3.75 0 010-5.303m5.304-.001a3.75 3.75 0 010 5.304m-7.425 2.122a6.75 6.75 0 010-9.546m9.546 0a6.75 6.75 0 010 9.546M5.106 18.894c-3.808-3.808-3.808-9.98 0-13.789m13.788 0c3.808 3.808 3.808 9.981 0 13.79M12 12h.008v.007H12V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
        </svg>
      </div>

      <h1 className="text-3xl font-bold text-auris-dark">Pure-Tone Hearing Test</h1>
      <p className="text-gray-600 text-lg">
        This test measures your hearing thresholds across {FREQUENCIES.length} frequencies —
        the same range used in clinical audiometry.
      </p>

      <div className="bg-white rounded-xl border border-gray-200 p-6 text-left space-y-4">
        <h2 className="font-semibold text-auris-dark">How it works</h2>
        <ol className="space-y-2 text-gray-600 list-decimal list-inside">
          <li>A tone starts completely silent and gradually gets louder.</li>
          <li>The moment you hear it, click <strong>"I Can Hear It"</strong> immediately.</li>
          <li>Frequencies are presented in random order — no two identical tones in a row.</li>
          <li>Each frequency is tested 3–5 times across the session to confirm accuracy.</li>
          <li>The test can cover both ears or a single ear of your choice.</li>
        </ol>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
        <strong>Before you start:</strong> Wear headphones or earbuds in a quiet room.
        Set your device volume to a comfortable listening level.
        This test is informational and does not replace a clinical audiogram.
      </div>

      <div className="space-y-3">
        <p className="text-sm font-medium text-gray-600">Which ear(s) do you want to test?</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => startTest('left')}
            className="flex-1 border-2 border-blue-300 text-blue-700 bg-blue-50 px-6 py-3 rounded-lg font-semibold hover:bg-blue-100 transition-colors"
          >
            ◀ Left Ear Only
          </button>
          <button
            onClick={() => startTest('both')}
            className="flex-1 bg-auris-teal text-white px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors shadow-sm"
          >
            Both Ears
          </button>
          <button
            onClick={() => startTest('right')}
            className="flex-1 border-2 border-red-300 text-red-700 bg-red-50 px-6 py-3 rounded-lg font-semibold hover:bg-red-100 transition-colors"
          >
            Right Ear Only ▶
          </button>
        </div>
      </div>
    </div>
  );

  // ── Render: Testing ───────────────────────────────────────────────────────

  const renderTesting = () => {
    const doneCount = countDone(displayMeasurements);
    const overallProgress = doneCount / FREQUENCIES.length;

    return (
      <div className="max-w-xl mx-auto space-y-5">
        {/* Ear indicator */}
        <div className={`flex items-center justify-center space-x-3 py-3 rounded-xl ${
          currentEar === 'left' ? 'bg-blue-50 border border-blue-200' : 'bg-red-50 border border-red-200'
        }`}>
          <span className={`text-xl font-bold ${currentEar === 'left' ? 'text-blue-600' : 'text-red-600'}`}>
            {currentEar === 'left' ? '◀ Left Ear' : 'Right Ear ▶'}
          </span>
        </div>

        {/* Overall progress */}
        <div className="space-y-1">
          <div className="flex justify-between text-sm text-gray-500">
            <span>Frequencies measured: {doneCount} / {FREQUENCIES.length}</span>
            <span>{Math.round(overallProgress * 100)}%</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-auris-teal transition-all duration-500"
              style={{ width: `${overallProgress * 100}%` }}
            />
          </div>
        </div>

        {/* Frequency chips */}
        <div className="flex flex-wrap gap-2 justify-center">
          {FREQUENCIES.map(f => {
            const done = isFreqDone(f, displayMeasurements);
            const count = (displayMeasurements[f] || []).length;
            const active = f === currentFreq;
            return (
              <div
                key={f}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  active
                    ? 'bg-auris-teal text-white border-auris-teal'
                    : done
                    ? 'bg-green-100 text-green-700 border-green-300'
                    : count > 0
                    ? 'bg-blue-50 text-blue-600 border-blue-200'
                    : 'bg-gray-100 text-gray-400 border-gray-200'
                }`}
              >
                {freqLabel(f)} Hz {done ? '✓' : count > 0 ? `(${count})` : ''}
              </div>
            );
          })}
        </div>

        {/* Current frequency card */}
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center space-y-4">
          <p className="text-sm text-gray-400 uppercase tracking-wide">Now testing</p>
          <p className="text-5xl font-bold text-auris-dark">
            {currentFreq !== null
              ? currentFreq >= 1000 ? `${currentFreq / 1000} kHz` : `${currentFreq} Hz`
              : '—'}
          </p>

          {/* Volume ramp bar */}
          <div className="space-y-1 pt-1">
            <p className="text-xs text-gray-400">Volume</p>
            <div className="h-3 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
              <div
                className="h-full bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 transition-none"
                style={{ width: `${rampProgress * 100}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-300">
              <span>silent</span><span>loud</span>
            </div>
          </div>

          {isPlaying
            ? <p className="text-sm text-gray-500 animate-pulse">Tone is playing…</p>
            : <p className="text-sm text-gray-400">Preparing next tone…</p>
          }
        </div>

        {/* Main action button */}
        <button
          onClick={handleCanHear}
          disabled={!isPlaying}
          className={`w-full py-7 rounded-xl text-2xl font-bold transition-all shadow-sm select-none ${
            isPlaying
              ? 'bg-auris-teal text-white hover:bg-opacity-90 active:scale-[0.98] cursor-pointer'
              : 'bg-gray-100 text-gray-300 cursor-not-allowed'
          }`}
        >
          I Can Hear It
        </button>

        <p className="text-center text-xs text-gray-400">
          Click the moment the tone becomes audible — even if very faint.
        </p>
      </div>
    );
  };

  // ── Render: Ear break ─────────────────────────────────────────────────────

  const renderEarBreak = () => (
    <div className="max-w-xl mx-auto text-center space-y-6">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
        <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-auris-dark">Left ear complete!</h2>
      <p className="text-gray-600">
        Now switch to your <strong>right ear</strong>. Move the headphone from your left ear to your right.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={startRightEar}
          className="bg-auris-teal text-white px-8 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors shadow-sm"
        >
          Test Right Ear
        </button>
        <button
          onClick={() => setPhase('results')}
          className="border border-gray-300 text-gray-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
        >
          Skip — Show Results
        </button>
      </div>
    </div>
  );

  // ── Render: Results ───────────────────────────────────────────────────────

  const renderResults = () => {
    const interpretThreshold = (dbHL: number): { label: string; color: string } => {
      if (dbHL <= 25) return { label: 'Normal', color: 'text-green-600' };
      if (dbHL <= 40) return { label: 'Mild loss', color: 'text-yellow-600' };
      if (dbHL <= 55) return { label: 'Moderate loss', color: 'text-orange-500' };
      if (dbHL <= 70) return { label: 'Moderately severe', color: 'text-red-500' };
      return { label: 'Severe loss', color: 'text-red-700' };
    };

    const avgLeft = Object.values(leftThresholds).length > 0
      ? gainToDbHL(median(Object.values(leftThresholds))) : null;
    const avgRight = Object.values(rightThresholds).length > 0
      ? gainToDbHL(median(Object.values(rightThresholds))) : null;

    return (
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="text-center space-y-2">
          <h2 className="text-3xl font-bold text-auris-dark">Your Audiogram</h2>
          <p className="text-gray-500">Test completed</p>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 gap-4">
          {[
            { ear: 'Left', avg: avgLeft, color: 'blue' },
            { ear: 'Right', avg: avgRight, color: 'red' },
          ].map(({ ear, avg, color }) => {
            const interp = avg !== null ? interpretThreshold(avg) : null;
            return (
              <div key={ear} className="bg-white rounded-xl border border-gray-200 p-5 text-center">
                <p className={`text-sm font-medium text-${color}-600 mb-1`}>{ear} Ear</p>
                <p className="text-3xl font-bold text-auris-dark">
                  {avg !== null ? `${Math.round(avg)} dBHL` : '—'}
                </p>
                {interp && <p className={`text-sm mt-1 font-medium ${interp.color}`}>{interp.label}</p>}
                <p className="text-xs text-gray-400 mt-1">average threshold</p>
              </div>
            );
          })}
        </div>

        {/* Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <Line data={chartData} options={chartOptions} />
        </div>

        {/* Frequency table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
            <h3 className="font-semibold text-auris-dark text-sm">Detailed Thresholds</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 uppercase">
                <th className="px-5 py-2 text-left">Frequency</th>
                <th className="px-5 py-2 text-center text-blue-600">Left (dBHL)</th>
                <th className="px-5 py-2 text-center text-red-600">Right (dBHL)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {FREQUENCIES.map(f => {
                const lDb = leftThresholds[f] !== undefined ? Math.round(gainToDbHL(leftThresholds[f])) : '—';
                const rDb = rightThresholds[f] !== undefined ? Math.round(gainToDbHL(rightThresholds[f])) : '—';
                return (
                  <tr key={f} className="hover:bg-gray-50">
                    <td className="px-5 py-2 font-medium text-auris-dark">
                      {f >= 1000 ? `${f / 1000} kHz` : `${f} Hz`}
                    </td>
                    <td className="px-5 py-2 text-center text-blue-700">{lDb}</td>
                    <td className="px-5 py-2 text-center text-red-600">{rDb}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-gray-400 text-center">
          dBHL values are approximate — relative to device output, not calibrated SPL.
          This is not a medical diagnosis. Consult an audiologist for clinical evaluation.
        </p>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {savedResultId ? (
            <div className="flex items-center justify-center space-x-2 text-green-600 font-medium py-3">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              <span>Saved to your profile</span>
            </div>
          ) : user ? (
            <button
              onClick={saveResults}
              disabled={isSaving}
              className="bg-auris-teal text-white px-8 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors disabled:opacity-50 shadow-sm"
            >
              {isSaving ? 'Saving…' : 'Save to Profile'}
            </button>
          ) : (
            <div className="flex flex-col items-center py-1">
              <p className="text-sm text-gray-500">Sign in to save your audiogram to your profile.</p>
            </div>
          )}
          <button
            onClick={() => {
              stopTone();
              setPhase('instructions');
              setSavedResultId(null);
            }}
            className="border border-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
          >
            Retake Test
          </button>
        </div>
      </div>
    );
  };

  // ── Main render ────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-auris-grey py-10 px-4">
      <div className="max-w-4xl mx-auto">
        {phase === 'instructions' && renderInstructions()}
        {phase === 'testing'      && renderTesting()}
        {phase === 'ear_break'    && renderEarBreak()}
        {phase === 'results'      && renderResults()}
      </div>
    </div>
  );
};

export default HearingTest;
